import { useState, useCallback, useEffect } from "react";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { CommonTableColumns } from "@/modules/common/types";

export const useFilterRenderer = (
  column: CommonTableColumns,
  isLoadingFilter?: boolean,
  filters?: any,
  setFilters?: (filters: {}) => void,
  setIsTableChange?: (isTableChange: boolean) => void,
  clearMultiselectValues?: boolean
) => {
  const [filterValue, setFilterValue] = useState<
    string | number | (string | number)[]
  >();

  const [filterValueTo, setFilterValueTo] = useState<
    string | number | (string | number)[]
  >();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [multiSelectValues, setMultiSelectValues] = useState<
    (string | number)[]
  >(
    (column.filterKey && Array.isArray(filters[column.filterKey])
      ? filters[column.filterKey]
      : []) || []
  );
  const [fetchedOptions, setFetchedOptions] = useState<any[]>([]);

  const screen = useScreenSize();

  const isFilterActive = useCallback(() => {
    return (
      (filters && column.filterKey && filters[column.filterKey]) ||
      (filters && column.filterKeyTo && filters[column.filterKeyTo])
    );
  }, [column.filterKey, filters, column.filterKeyTo]);

  const clearCurrentFilter = useCallback(() => {
    setIsTableChange && setIsTableChange(true);
    setFilterValue(undefined);
    setFilterValueTo(undefined);
    setStartDate(null);
    setEndDate(null);
    setMultiSelectValues([]);
    column?.onClearFilter?.();

    setFilters &&
      setFilters((prevFilters: any) => {
        const { [`${column.filterKey}`]: removedFilter, ...restFilters } =
          prevFilters;
        return restFilters;
      });

    if (column.filterKeyFrom || column.filterKeyTo) {
      setFilters &&
        setFilters((prevFilters: any) => {
          const {
            [`${column.filterKeyFrom}`]: removedFilterFrom,
            [`${column.filterKeyTo}`]: removedFilterTo,
            ...restFilters
          } = prevFilters;

          return restFilters;
        });
    }
  }, [
    column.filterKey,
    column.filterKeyFrom,
    column.filterKeyTo,
    setFilters,
    setIsTableChange,
  ]);

  const fetchOptions = useCallback(async () => {
    if (column.fetchOptions) {
      try {
        const options = await column.fetchOptions();
        setFetchedOptions(options);
      } catch (error) {
        console.error("Error fetching options:", error);
        setFetchedOptions([]);
      }
    }
  }, [column.fetchOptions]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    if (clearMultiselectValues == true) {
      setFilterValue(undefined);
    }

    if (!filters) return;

    const filterType = column.filter;
    const filterKey = column.filterKey;

    const simpleFilterTypes = ["text", "select", "textRange"];
    if (
      filterKey &&
      filters[filterKey] &&
      simpleFilterTypes.includes(String(filterType))
    ) {
      setFilterValue(filters[filterKey]);
    }

    if (filterType === "date" && column.filterKeyFrom && column.filterKeyTo) {
      const fromValue = filters[column.filterKeyFrom];
      const toValue = filters[column.filterKeyTo];

      if (fromValue) {
        const parsedStartDate = new Date(fromValue);
        if (!isNaN(parsedStartDate.getTime())) setStartDate(parsedStartDate);
      }

      if (toValue) {
        const parsedEndDate = new Date(toValue);
        if (!isNaN(parsedEndDate.getTime())) setEndDate(parsedEndDate);
      }
    }

    if (filterType === "multiSelect" && filterKey) {
      const rawValue = filters[filterKey];
      if (rawValue) {
        const normalizeToArray = (value: unknown): (string | number)[] =>
          (Array.isArray(value) ? value : [value])
            .flatMap((v) => String(v).split(","))
            .map((v) => v.trim())
            .filter(Boolean);

        setMultiSelectValues(normalizeToArray(rawValue));
      }
    }

    if (column.filterKeyTo && filters[column.filterKeyTo]) {
      setFilterValueTo(filters[column.filterKeyTo]);
    }

    if (Object.keys(filters).length === 0) {
      setFilterValue(undefined);
      setFilterValueTo(undefined);
      setMultiSelectValues([]);
      setStartDate(null);
      setEndDate(null);
    }
  }, [
    clearMultiselectValues,
    filters,
    column.filterKey,
    column.filter,
    column.filterKeyTo,
    column.filterKeyFrom,
  ]);

  const onFilterChange = (value: string | number | []) => {
    setIsTableChange && setIsTableChange(false);
    if (setIsTableChange) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setFilters &&
          setFilters((prevFilters: any) => {
            const { [String(column.filterKey)]: removed, ...rest } =
              prevFilters;
            return rest;
          });
      } else {
        setFilters &&
          setFilters((prevFilters: any) => ({
            ...prevFilters,
            [String(column.filterKey)]: [value],
          }));
      }
      setFilterValue(value);
    } else {
      setFilterValue(value);
    }
  };

  const onFilterChangeTo = (value: string | number | []) => {
    setIsTableChange && setIsTableChange(false);
    if (setIsTableChange) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setFilters &&
          setFilters((prevFilters: any) => {
            const { [String(column.filterKeyTo)]: removed, ...rest } =
              prevFilters;
            return rest;
          });
      } else {
        setFilters &&
          setFilters((prevFilters: any) => ({
            ...prevFilters,
            [String(column.filterKeyTo)]: [value],
          }));
      }
      setFilterValueTo(value);
    } else {
      setFilterValueTo(value);
    }
  };

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleMultiSelectChange = (selectedOptions: (string | number)[]) => {
    setMultiSelectValues(selectedOptions);
  };

  const handleAutoCompleteChange = (selectedOptions: (string | number)[]) => {
    setIsTableChange && setIsTableChange(false);
    const updatedFilterValue = selectedOptions.join(",");

    if (!updatedFilterValue) {
      setFilters &&
        setFilters((prevFilters: any) => {
          const { [String(column.filterKey)]: removed, ...rest } = prevFilters;
          return rest;
        });
    } else {
      setFilters &&
        setFilters((prevFilters: any) => ({
          ...prevFilters,
          [String(column.filterKey)]: [updatedFilterValue],
        }));
    }
  };
  const handleRemoveMultiSelect = (valueToRemove: string | number) => {
    setIsTableChange && setIsTableChange(false);

    const currentFilterValue =
      filters &&
      column.filterKey &&
      filters[column.filterKey] &&
      filters[column.filterKey][0]
        ? filters[column.filterKey][0]
        : "";

    const filterArray =
      typeof currentFilterValue === "string"
        ? currentFilterValue.split(",").map(Number)
        : [];

    const updatedFilterArray = filterArray.filter(
      (val: number) => val !== valueToRemove
    );

    const updatedFilterValue =
      updatedFilterArray.length > 0 ? updatedFilterArray.join(",") : "";

    setFilters &&
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        [String(column.filterKey)]: updatedFilterValue
          ? [updatedFilterValue]
          : [""],
      }));
  };

  const applyFilter = useCallback(() => {
    if (column.filter === "date") {
      column.onFilter &&
        column.onFilter({
          filterKey: column?.filterKey!,
          filterKeyFrom: column?.filterKeyFrom!,
          filterKeyTo: column?.filterKeyTo!,
          startDateValue: startDate?.toLocaleDateString()!,
          endDateValue: endDate?.toLocaleDateString()!,
        });

      column.onFilterTo &&
        column.onFilterTo({
          filterKey: column?.filterKeyTo!,
          startDateValue: startDate?.toLocaleDateString("en-CA")!,
          endDateValue: endDate?.toLocaleDateString("en-CA")!,
        });
      column.onFilterFrom &&
        column.onFilterFrom({
          filterKey: column?.filterKeyFrom!,
          startDateValue: startDate?.toLocaleDateString("en-CA")!,
          endDateValue: endDate?.toLocaleDateString("en-CA")!,
        });
    } else if (column.filter === "multiSelect") {
      column.onFilter &&
        column.onFilter({
          filterKey: column?.filterKey!,
          filterValue: multiSelectValues,
        });
    } else if (column.filter === "autoComplete") {
      column.onFilter &&
        column.onFilter({
          filterKey: column?.filterKey!,
          filterValue: column.filterKey
            ? filters?.[column.filterKey]?.[0] ?? ""
            : "",
        });
    } else if (column.filter === "textRange") {
      column.onFilterTo &&
        column.onFilterTo({
          filterKey: column?.filterKeyTo!,
          filterValue: [filterValueTo]!,
        });
      column.onFilterFrom &&
        column.onFilterFrom({
          filterKey: column?.filterKeyFrom!,
          filterValue: [filterValue]!,
        });
    } else {
      column.onFilter &&
        column.onFilter({
          filterKey: column?.filterKey!,
          filterValue: [filterValue]!,
        });
    }
  }, [
    column,
    startDate,
    endDate,
    multiSelectValues,
    filterValue,
    filterValueTo,
    filters,
  ]);

  return {
    filterValue,
    filterValueTo,
    setFilterValue,
    startDate,
    endDate,
    multiSelectValues,
    fetchedOptions,
    screen,
    isFilterActive,
    clearCurrentFilter,
    onFilterChange,
    onFilterChangeTo,
    handleDateRangeChange,
    handleMultiSelectChange,
    applyFilter,
    setStartDate,
    setEndDate,
    setMultiSelectValues,
    handleRemoveMultiSelect,
    handleAutoCompleteChange,
    isLoadingFilter,
  };
};
